/**
 * Build a sensible download filename when the server sends a UUID or omits Content-Disposition.
 */

export function looksLikeUuidFileStem(name: string): boolean {
  const stem = name.trim().replace(/\.[^.]+$/, '');
  const candidate = stem || name.trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(candidate);
}

function extensionFromMime(blob: Blob): string {
  if (!blob.type || blob.type === 'application/octet-stream') return '';
  const m: Record<string, string> = {
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'text/plain': '.txt',
    'text/csv': '.csv',
    'image/jpeg': '.jpg',
    'image/png': '.png',
  };
  return m[blob.type] ?? '';
}

export interface ResolveDownloadNameOptions {
  /** Parsed from Content-Disposition, if any */
  headerFileName: string | null;
  blob: Blob;
  /** Typical DB / UI file name (may be a storage UUID) */
  fileName?: string | null;
  /** Human title when fileName is not usable */
  documentTitle?: string | null;
  /** e.g. approval context source doc title */
  sourceDocumentTitle?: string | null;
}

/** First candidate that is non-empty and does not look like a storage UUID. */
export function firstNonUuidDisplayName(
  ...candidates: Array<string | null | undefined>
): string {
  for (const c of candidates) {
    const t = c?.trim();
    if (t && !looksLikeUuidFileStem(t)) {
      return t;
    }
  }
  return 'document';
}

/**
 * Prefer a real Content-Disposition name; fall back to title/fileName with MIME-based extension.
 */
export function resolveDownloadFileName(opts: ResolveDownloadNameOptions): string {
  const { headerFileName, blob, fileName, documentTitle, sourceDocumentTitle } = opts;
  const trimmed = headerFileName?.trim();
  if (trimmed) {
    const stem = trimmed.replace(/\.[^.]+$/, '');
    if (!looksLikeUuidFileStem(stem) && !looksLikeUuidFileStem(trimmed)) {
      return trimmed;
    }
  }

  const base = firstNonUuidDisplayName(fileName, documentTitle, sourceDocumentTitle);
  if (/\.\w+$/.test(base)) return base;
  const ext = extensionFromMime(blob);
  return base + (ext || '.bin');
}
